import Styles from './SelectImage.module.scss'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { AI, LinkAngled, Upload5 } from 'blaze-ui/icons'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useStateRef } from 'hooks/useStateRef'
import { useEffect, useState } from 'react'
import { useField } from 'formik'
import { StockImages } from './StockImages'
import { LibraryImages } from './LibraryImages'
import { ConfirmImage } from './ConfirmImage'
import { SelectedImage } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AiGeneration } from 'components/ImagePickerModal/AIGeneration/AiGeneration'
import { Stock } from 'components/ImagePickerModal/Stock/Stock'
import { Link } from 'components/ImagePickerModal/Link/Link'
import { BrandKitImages } from 'components/Wizards/DesignWizard/AddPhoto/SelectImage/BrandKitImages'

export const SelectImage = () => {
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)
  const [showConfirmImage, setShowConfirmImage] = useState<boolean>()
  const [lastSelectedImage, setLastSelectedImage] = useState<SelectedImage>()
  const imagePickerContext = useImagePickerContext()
  const { setValue: setMediaFileId } = useField('initial_media_file_id')[2]
  const { value: brandKitId } = useField<number>('brand_kit_id')[0]
  const { selectedImages, setProvider, provider, onUploadStart, uploadImages, accept, setSelectedImages } =
    imagePickerContext

  const handleOnChange = async (files: File[]) => {
    const toast = showToastMessage('Uploading...', 'loading', undefined, 0)
    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)
    toast()
    onSelectImage(uploadedImages[0])
  }

  const onSelectImage = (image: SelectedImage) => {
    setSelectedImages([image])
    setShowConfirmImage(true)
  }

  const onBack = () => {
    setLastSelectedImage(selectedImages[0])
    setShowConfirmImage(false)
  }

  useEffect(() => {
    setMediaFileId(selectedImages?.[0]?.id || selectedImages?.[0]?.unsplash?.id)
  }, [selectedImages]) // eslint-disable-line react-hooks/exhaustive-deps

  if (showConfirmImage)
    return <ConfirmImage imageUrl={selectedImages?.[0]?.preview_url || selectedImages?.[0]?.url} onBack={onBack} />

  if (provider === 'ai') {
    return <AiGeneration />
  }

  if (provider === 'stock') {
    return <Stock />
  }

  if (provider === 'link') {
    return <Link />
  }

  const ImagesComponent = brandKitId ? BrandKitImages : LibraryImages

  return (
    <div className={Styles.selectImageContainer}>
      <div className={Styles.sections}>
        <StockImages />
        <ImagesComponent onSelectImage={onSelectImage} lastSelectedImage={lastSelectedImage} brandKitId={brandKitId} />
      </div>
      <div className={Styles.actions}>
        <Button frontIcon={Upload5} variant="secondary" onPress={() => fileInputRef?.click()}>
          Upload Image
        </Button>
        <Button frontIcon={AI} variant="secondary" onPress={() => setProvider('ai')}>
          Generate from AI
        </Button>
        <Button frontIcon={LinkAngled} variant="secondary" onPress={() => setProvider('link')}>
          Get image from a link
        </Button>
      </div>

      <VisuallyHidden>
        <input
          type="file"
          accept={accept}
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            handleOnChange(files)
          }}
          ref={setFileInputRef}
          multiple={false}
          name="insert image"
          id="insert image"
          data-testid="file-input"
        />
      </VisuallyHidden>
    </div>
  )
}
